<template>
  <div class="h-[67vh] overflow-y-auto p-6">
    <p class="text-heading-title mb-3">{{ t("be_safe") }}</p>
    <div class="flex">
      <p class="text-body-default">
        {{ t("insurance_text_1", { value: standardCoverage }) }}
      </p>
      <a
        class="text-body-default ml-1 underline"
        href="https://www.homerunner.com/handelsbetingelser/"
        target="_blank"
        rel="noopener noreferrer"
        >{{ t("as_our_terms") }}</a
      >
    </div>
    <p class="text-body-default mb-5">{{ t("insurance_text_2") }}</p>

    <!-- The products failed to load. There is no partial data to fall back to: showing DKK
         amounts under another currency is exactly the bug this replaces. -->
    <div v-if="failed" class="bg-bgr-secondary mb-5 flex flex-col items-center gap-3 rounded-xl p-6 text-center">
      <p class="text-body-large-m">{{ t("insurance_could_not_be_loaded") }}</p>
      <p class="text-body-default">{{ errorMessage }}</p>
      <Button
        v-if="currencyIsSupported"
        type="button"
        class="w-max"
        :variant="ButtonVariant.Secondary"
        @click="loadInsurances"
        >{{ t("try_again") }}</Button
      >
    </div>

    <div v-else class="mb-5 grid grid-cols-4 gap-4">
      <template v-if="loading && !insuranceData">
        <Bone
          v-for="i in 4"
          :key="i"
          class="shadow-inset-00-01 flex h-full min-h-[400px] w-auto flex-col rounded-lg p-3 transition-all duration-300"
          type="light"
        />
      </template>

      <div
        v-for="(insurance, index) in insuranceData"
        :key="insurance.id ?? 'included'"
        class="shadow-inset-00-01 hover:shadow-inset-01-01 flex h-full min-h-[400px] w-auto cursor-pointer flex-col rounded-lg p-3 transition-all duration-300"
        :class="isSelected(insurance) ? 'shadow-inset-01-01' : ''"
        @click="() => selectInsurance(insurance)"
      >
        <div class="relative h-28 w-full">
          <img v-if="findColor(index) == 'purple'" src="~/assets/illustrations/backgrounds/violet_grid.svg" alt="" />
          <img v-if="findColor(index) == 'brown'" src="~/assets/illustrations/backgrounds/dijon_grid.svg" alt="" />
          <img v-if="findColor(index) == 'pink'" src="~/assets/illustrations/backgrounds/pink_grid.svg" alt="" />
          <img v-if="findColor(index) == 'orange'" src="~/assets/illustrations/backgrounds/coral_grid.svg" alt="" />
          <div class="absolute top-0 left-0">
            <Avatar :entity="{ name: insurance.title }" :color="findColor(index)" :size="ComponentSize.sm" />
          </div>
        </div>
        <p class="text-body-large">{{ t("up_to") }}</p>
        <p class="text-heading-section mb-5">{{ useFormatPrice(insurance.coverage, true, insurance.currency) }}</p>
        <p class="text-body-large">{{ returnInsuranceText(index) }}</p>
        <div class="mt-auto flex w-full">
          <p class="text-body-default">
            {{ t("price") }}
            <br />
            <span class="text-body-large-m">{{ returnPrice(insurance) }}</span>
          </p>
        </div>
      </div>
    </div>
    <p class="text-body-small">{{ t("insurance_disclaimer") }}</p>
  </div>
</template>

<script setup lang="ts">
  import type { Insurance } from "~/types/shipment";

  import { insuranceCurrencyIsSupported } from "../../composables/useInsurance";

  const { t } = useI18n();

  const props = defineProps<{
    values: any;
  }>();

  const { loading, error, fetchInsurances } = useInsurance();

  const insuranceData = ref<Insurance[] | null>(null);

  /**
   * The currency the shipment is being quoted in, taken from the selected carrier product.
   * The API converts coverage and fees into it and labels every item with it.
   */
  const quoteCurrency = computed<string | null>(() => props.values.carrier_product_currency || null);

  const currencyIsSupported = computed(() => insuranceCurrencyIsSupported(quoteCurrency.value));

  const failed = computed(() => error.value !== null && !insuranceData.value);

  const errorMessage = computed(() => {
    // A currency with no rate row is a permanent gap, not a transient failure - say so.
    if (!currencyIsSupported.value) {
      return t("insurance_not_available_in_currency", { currency: quoteCurrency.value });
    }
    return error.value || t("something_went_wrong");
  });

  const loadInsurances = () => {
    fetchInsurances(props.values, quoteCurrency.value).then((data) => {
      insuranceData.value = data;
      resyncSelection();
    });
  };

  const { value: selectedInsurance } = useField(() => "insurance", undefined, {
    syncVModel: true,
  });

  const { value: selectedInsuranceCoverage } = useField(() => "insurance_coverage", undefined, {
    syncVModel: true,
  });

  const { value: selectedInsurancePrice } = useField(() => "insurance_price", undefined, {
    syncVModel: true,
  });

  const { value: selectedInsuranceCurrency } = useField(() => "insurance_currency", undefined, {
    syncVModel: true,
  });

  /** The included-cover entry has no id - there is nothing to buy, so it books as 0. */
  const insuranceId = (insurance: Insurance) => insurance.id ?? 0;

  const isSelected = (insurance: Insurance) =>
    selectedInsurance.value !== undefined && String(selectedInsurance.value) === String(insuranceId(insurance));

  const selectInsurance = (insurance: Insurance) => {
    selectedInsurance.value = insuranceId(insurance);
    // Raw amounts plus the currency they are in - formatting happens where they are rendered.
    selectedInsuranceCoverage.value = insurance.coverage;
    selectedInsurancePrice.value = useDecidePriceExclOrIncl(insurance);
    selectedInsuranceCurrency.value = insurance.currency;
  };

  const clearInsurance = () => {
    selectedInsurance.value = undefined;
    selectedInsuranceCoverage.value = undefined;
    selectedInsurancePrice.value = undefined;
    selectedInsuranceCurrency.value = undefined;
  };

  /**
   * A carrier or weight change re-prices the products, and can change the currency they are
   * quoted in. Re-read the stored amounts off the fresh list so the summary never shows a
   * figure from the previous carrier, and drop a selection the new list no longer offers.
   */
  const resyncSelection = () => {
    if (selectedInsurance.value === undefined || !insuranceData.value) return;

    const stillOffered = insuranceData.value.find((insurance) => isSelected(insurance));

    if (stillOffered) selectInsurance(stillOffered);
    else clearInsurance();
  };

  loadInsurances();

  const standardCoverage = computed(() => {
    const included = insuranceData.value?.[0];
    if (!included) return "";
    return useFormatPrice(included.coverage, true, included.currency);
  });

  const returnPrice = (insurance: Insurance) => {
    if (insurance.price_incl_tax == 0) {
      return t("included_by_carrier");
    }
    return useFormatPrice(useDecidePriceExclOrIncl(insurance), true, insurance.currency);
  };

  /**
   * Indexed rather than keyed on id: the first entry is the carrier's included cover and
   * carries no id at all.
   */
  const returnInsuranceText = (index: number) => {
    switch (index) {
      case 1:
        return t("insurance_text_with_value", { value: t("moderate_value").toLowerCase() });
      case 2:
        return t("insurance_text_with_value", { value: t("high_value").toLocaleLowerCase() });
      case 3:
        return t("insurance_text_with_value", { value: t("very_high_value").toLowerCase() });
      default:
        return t("free_insurance");
    }
  };

  const findColor = (index: number) => {
    switch (index) {
      case 1:
        return "orange";
      case 2:
        return "pink";
      case 3:
        return "purple";
      default:
        return "brown";
    }
  };
</script>
